import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function TestimonialSubmit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ author_name: "", author_role: "", division: "couch", rating: 5, content: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/testimonials", form);
      setDone(true);
      toast.success("Thank you for sharing your story.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Page>
        <section className="md-section">
          <div className="md-container max-w-xl text-center">
            <CheckCircle size={64} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto mb-5" />
            <h1 className="text-4xl font-serif mb-3">Thank you, truly</h1>
            <p className="text-base mb-6" style={{ color: "var(--md-text-soft)" }}>
              Your words are now with our team. After a quick read for clarity and privacy, your story may appear on our home page to encourage others on a similar path.
            </p>
            <Link to="/" className="btn-primary" data-testid="story-thanks-home">Back to Home <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </section>
      </Page>
    );
  }

  return (
    <Page>
      <section className="md-section">
        <div className="md-container max-w-2xl">
          <div className="md-label-eyebrow mb-3">Share Your Story</div>
          <h1 className="text-4xl md:text-5xl font-serif mb-3">Your voice, gently held</h1>
          <p className="text-base mb-8" style={{ color: "var(--md-text-soft)" }}>
            Tell us how MindDhara has helped you. We&apos;ll review every submission with care before publishing. You can stay anonymous, just leave the name blank.
          </p>

          <form onSubmit={submit} className="md-card space-y-5" data-testid="testimonial-form">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="md-label-eyebrow block mb-1.5">Your name (or leave blank for anonymous)</label>
                <input className="md-input" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="story-name" placeholder="Aanya R." />
              </div>
              <div>
                <label className="md-label-eyebrow block mb-1.5">Your role</label>
                <input className="md-input" value={form.author_role} onChange={(e) => setForm({ ...form, author_role: e.target.value })} data-testid="story-role" placeholder="Therapy client / Mentee / Corporate lead" />
              </div>
            </div>
            <div>
              <label className="md-label-eyebrow block mb-1.5">Which arm did you experience?</label>
              <select className="md-input" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} data-testid="story-division">
                <option value="couch">Couch, Therapy</option>
                <option value="forward">Forward, Mentorship</option>
                <option value="events">Events / Workshops</option>
                <option value="shop">Shop / Resources</option>
              </select>
            </div>
            <div>
              <label className="md-label-eyebrow block mb-1.5">How would you rate the experience?</label>
              <div className="flex items-center gap-1" data-testid="story-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} star`} data-testid={`story-rating-${n}`} className="story-star-btn">
                    <Star size={28} weight={n <= form.rating ? "fill" : "regular"} style={{ color: n <= form.rating ? "#F4C95D" : "var(--md-text-muted)" }} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="md-label-eyebrow block mb-1.5">Your story</label>
              <textarea required minLength={20} className="md-input" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="story-content" placeholder="Tell us what shifted for you, focus, sleep, relationships, clarity at work…" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="story-submit">
              {busy ? "Submitting…" : "Share My Story"} <ArrowRight size={14} weight="bold" />
            </button>
            <p className="text-xs" style={{ color: "var(--md-text-muted)" }}>
              By submitting, you allow MindDhara to publish your testimonial on our website. We never share your email or sensitive details.
            </p>
          </form>
        </div>
      </section>
    </Page>
  );
}
